import { create } from "zustand";

import * as Events from "@app/analytics/tracking/events";
import type { DailyReadStatus } from "@app/analytics/types";

interface DailyReadStateValues {
  id: string | null;
  audioEl: HTMLAudioElement | null;
  autoPlayOnLoad: boolean;
  currentTimestamp: number;
  duration: number;
  firstPlayLogged: boolean;
  minimized: boolean;
  muted: boolean;
  paused: boolean;
  promptFeedback: boolean;
  status: DailyReadStatus;
}

interface DailyReadState extends DailyReadStateValues {
  /* Audio player controls */
  maximize: () => void;
  minimize: () => void;
  mute: () => void;
  pause: () => void;
  play: () => void;
  skipBackward: () => void;
  skipForward: () => void;
  unmute: () => void;

  /* Setters */
  initializeDailyRead: (opts: {
    id: string;
    minimized: boolean;
    audioEl: HTMLAudioElement | null;
    status: DailyReadStatus;
  }) => void;
  setAudioEl: (audioEl: HTMLAudioElement | null) => void;
  setAutoPlayOnLoad: (autoPlayOnLoad: boolean) => void;
  setCurrentTimestamp: (currentTimestamp: number, ended?: boolean) => void;
  setDuration: (duration: number) => void;
}

// percentage of the duration at which we will prompt the user for feedback
const PROMPT_FEEDBACK_THRESHOLD = 75;

const DEFAULT_STATE: DailyReadStateValues = {
  id: null,
  audioEl: null,
  autoPlayOnLoad: false,
  currentTimestamp: 0,
  duration: 0,
  firstPlayLogged: false,
  minimized: false,
  muted: false,
  paused: true,
  promptFeedback: false,
  status: null,
};

const shouldPromptForFeedback = (duration: number, timestamp: number) => {
  return Math.round((timestamp / duration) * 100) >= PROMPT_FEEDBACK_THRESHOLD;
};

const useDailyReadStore = create<DailyReadState>((set) => ({
  ...DEFAULT_STATE,
  maximize: () => set({ minimized: false }),
  minimize: () =>
    set(() => {
      Events.dismissDailyReadPlayer();
      return { minimized: true };
    }),
  mute: () =>
    set((state) => {
      if (!state.audioEl) return {};
      state.audioEl.muted = true;
      return { muted: true };
    }),
  pause: () =>
    set((state) => {
      if (!state.audioEl) return {};
      state.audioEl.pause();
      return { paused: true };
    }),
  play: () =>
    set((state) => {
      if (!state.audioEl) return {};
      state.audioEl.play();
      if (!state.firstPlayLogged) {
        Events.playDailyRead();
      }
      return {
        firstPlayLogged: true,
        paused: false,
        status: null,
      };
    }),
  skipBackward: () =>
    set((state) => {
      if (!state.audioEl) return {};
      const currentTime = state.audioEl.currentTime;
      const newCurrentTime = Math.max(0, currentTime - 10);
      state.audioEl.currentTime = newCurrentTime;
      return { currentTimestamp: newCurrentTime };
    }),
  skipForward: () =>
    set((state) => {
      if (!state.audioEl) return {};
      const currentTime = state.audioEl.currentTime;
      const newCurrentTime = Math.min(state.audioEl.duration, currentTime + 10);
      const promptForFeedback =
        state.promptFeedback ||
        shouldPromptForFeedback(state.audioEl.duration, newCurrentTime);
      state.audioEl.currentTime = newCurrentTime;
      return { currentTimestamp: newCurrentTime, promptForFeedback };
    }),
  unmute: () =>
    set((state) => {
      if (!state.audioEl) return {};
      state.audioEl.muted = false;
      return { muted: false };
    }),

  setAudioEl: (audioEl) =>
    set({
      audioEl,
      duration: !audioEl || isNaN(audioEl.duration) ? 0 : audioEl.duration,
    }),
  initializeDailyRead: ({ id, audioEl, minimized, status }) =>
    set({
      id,
      audioEl,
      duration: !audioEl || isNaN(audioEl.duration) ? 0 : audioEl.duration,
      firstPlayLogged: false,
      minimized,
      muted: false,
      paused: true,
      status,
    }),
  setAutoPlayOnLoad: (autoPlayOnLoad) => set({ autoPlayOnLoad }),
  setCurrentTimestamp: (currentTimestamp, ended) =>
    set((state) => {
      const promptFeedback =
        state.promptFeedback ||
        shouldPromptForFeedback(state.duration, currentTimestamp);
      return {
        currentTimestamp,
        promptFeedback,
        status: ended ? "listened" : state.status,
        paused: ended ? true : state.paused,
      };
    }),
  setDuration: (duration) => set({ duration }),
}));

export default useDailyReadStore;
